/**
 * Time zones, from the runtime rather than from a list we maintain (punch 4.1).
 *
 * O-25 was answered "no timezone column" while the portfolio was believed to be
 * Carolinas-only, and reversed when it was not — "we have properties all over
 * the world". So a property carries the IANA zone it stands in, and the hours on
 * it stop being wall-clock times with nothing to anchor them.
 *
 * ─── ONE AUTHORITY, AND WHY IT IS THIS ONE ────────────────────────────────
 *
 * There are two IANA-derived lists in this system and they disagree. Measured,
 * not assumed, on the stack this ships on:
 *
 *   * Postgres 16 knows 499 names, among them `Asia/Kolkata` and
 *     `America/Argentina/Buenos_Aires`;
 *   * Node 22's `Intl.supportedValuesOf('timeZone')` returns 418, and for those
 *     two zones returns the OTHER spelling — `Asia/Calcutta`,
 *     `America/Buenos_Aires` — because CLDR canonicalises to the pre-2018 names
 *     and IANA canonicalises to the newer ones.
 *
 * Each list holds names the other lacks, so validating in the database against
 * one of them would refuse values the picker offers: F-36's shape, self-
 * inflicted. `Intl.DateTimeFormat` is the authority instead, because it is the
 * one thing that accepts BOTH spellings and canonicalises case — the database
 * checks only the shape.
 */

export interface TimeZoneOption {
  /** The IANA name, exactly as stored. */
  readonly value: string;
  /** `America/New_York` reads as `America / New York` — searchable and human. */
  readonly label: string;
  /** `UTC−05:00`, resolved for the given moment so DST is honest. */
  readonly offset: string;
}

/**
 * `Area/Location`, the same rule 0075's CHECK constraint enforces — written here
 * as well so the two are visibly one rule rather than two that happen to agree.
 *
 * It exists because `Intl.DateTimeFormat` is MORE permissive than a building
 * needs. It accepts `-05:00` and `UTC`, and neither is a place: an offset cannot
 * say when the clocks change, which is the entire reason a property carries a
 * zone rather than a number. A record that said `UTC` would be claiming its
 * hours never move, in a portfolio that spans hemispheres.
 */
const ZONE_SHAPE = /^[A-Za-z][A-Za-z0-9_+-]*(\/[A-Za-z0-9_+-]+){1,2}$/;

/**
 * The stored spelling of a zone name, or null if the runtime does not know it.
 *
 * Everything about a zone name that a person can get wrong is fixed here:
 * surrounding space, letter case (`america/new_york` is accepted by
 * `Intl.DateTimeFormat` and would otherwise be stored as a third spelling of one
 * zone), and the alias/canonical split above. Two people picking the same city
 * in two browsers therefore end up with one value in the column.
 */
export function canonicalTimeZone(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  try {
    const resolved = new Intl.DateTimeFormat('en-US', {
      timeZone: trimmed,
    }).resolvedOptions().timeZone;
    return ZONE_SHAPE.test(resolved) ? resolved : null;
  } catch {
    return null;
  }
}

/**
 * `UTC−05:00` for a zone at a moment.
 *
 * AT A MOMENT, because half the world changes offset twice a year and a picker
 * that showed January's offset in July would be wrong for months at a time.
 * `longOffset` gives `GMT-05:00`; the label says UTC because that is what the
 * rest of the app calls it (docs/00: storage is UTC without exception), and the
 * minus is U+2212 so it reads as a sign rather than a hyphen.
 */
export function timeZoneOffset(zone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(at);
    const name = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    // `GMT` alone is how the formatter writes a zero offset.
    const offset = name === 'GMT' ? '+00:00' : name.replace('GMT', '');
    return `UTC${offset.replace('-', '−')}`;
  } catch {
    return '';
  }
}

/** `America/Indiana/Indianapolis` → `America / Indiana / Indianapolis`. */
export function timeZoneLabel(zone: string): string {
  return zone.replaceAll('_', ' ').replaceAll('/', ' / ');
}

/**
 * Every zone the runtime offers, ordered by name.
 *
 * Not memoised on purpose: `at` moves, offsets change with it, and a cached list
 * would show October's offsets in April. It is ~418 rows built once per picker
 * open, which is nothing next to being quietly six months out of date.
 */
export function timeZoneOptions(at: Date = new Date()): readonly TimeZoneOption[] {
  const names =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return names.map((value) => ({
    value,
    label: timeZoneLabel(value),
    offset: timeZoneOffset(value, at),
  }));
}
